import { Fragment, useState, useMemo, useCallback, useRef, useEffect, useContext } from 'react';
import {
    Flex, Box, Text, Skeleton, HStack, Spinner,
    Table, Thead, Tbody, Tr, Td, IconButton, Tooltip, Icon,
    Button, Switch, FormLabel, FormControl,
    Popover, PopoverTrigger, PopoverContent, PopoverBody, VStack, Input, InputGroup, InputLeftElement, Portal,
    Accordion, AccordionItem, AccordionButton, AccordionPanel, AccordionIcon,
    SimpleGrid, useDisclosure, Checkbox, Divider,
} from '@chakra-ui/react';
import AppDrawer from '../../../components/AppDrawer';
import { TbDownload, TbArrowUp, TbArrowDown, TbChevronDown, TbChevronRight, TbInfoCircle, TbLock, TbLockOpen, TbMaximize, TbMinimize, TbSettings, TbSearch, TbCheck, TbChartBar, TbTable, TbGripVertical, TbArrowBackUp, TbLayoutList, TbListDetails, TbColumns, TbEye, TbBuildingStore } from 'react-icons/tb';
import { getSourceColor, getSourceLabel } from '../../../components/SourceBadge';
import { MARKETPLACE_EXCLUDED_METRICS } from '../widgets/types';
import { useTableSettings } from '../../../hooks/useTableSettings';
import { useColumnWidths } from '../../../hooks/useColumnWidths';
import type { TableSettings } from '../../../hooks/useTableSettings';
import {
    COLUMN_CONFIG,
    defaultPickerRows,
    getChangeMapKey,
    getColumnLabel,
    getDefaultHiddenLeafKeys,
    getGroupForKey,
    getLeafKeysForDomain,
    getTableCellValue,
    migrateColumnPrefsInStorage,
    pickerRowsToPrefs,
    prefsToPickerRows,
    reorderPickerRows,
    togglePickerRowForDomain,
    migrateLegacyColumnKey,
    type ColumnPickerRow,
} from '../domainRankingTableColumns';
import type { ColumnPrefs } from '../../../hooks/useReportingColumnPrefs';
import { useReportingColumnPrefs } from '../../../hooks/useReportingColumnPrefs';
import { loadViewPrefs, saveViewPrefs } from '../../../hooks/reporting/useReportingViewPrefs';
import ExcludeEvsToggle from './ExcludeEvsToggle';
import ResizableTh from '../../../components/ResizableTh';
import ReportingFullScreenShell from './ReportingFullScreenShell';
import { DomainStatItem, DomainStatsTotals, DomainType, VendorGroupBy, ReportingComparison } from '../../../types/reporting';
import { ViewType } from './ViewToggle';
import { ChartMetricType, CHART_METRIC_OPTIONS, VENDOR_CHART_METRIC_OPTIONS, LOCATION_CHART_METRIC_OPTIONS, INSURANCE_CHART_METRIC_OPTIONS, OEM_CHART_METRIC_OPTIONS } from './ChartMetricSelector';
import VerticalRankingChart from './VerticalRankingChart';
import SortArrowIcon, { SortIconDirection } from '../../../components/SortArrowIcon';
import { isReportingComparisonSupported } from '../shared/reportingComparisonUtils';
import { compareMetricWithPriorTiebreaker } from '../../../hooks/reporting/reportingBundleDomainStats';
import SessionContext from '../../../contexts/SessionContext';
import { filterChartMetricsForInvoicing, isInvoicingGatedChartMetric, INVOICING_GATED_RANKING_LEAF_KEYS, orgShowsInvoicingReportingMetrics } from '../shared/invoicingGatedMetrics';

interface VariantGroup {
    label: string;
    vendorLabel?: string;
    variants: { value: string; label: string; }[];
}

const RANKING_VARIANT_GROUPS: VariantGroup[] = [
    {
        label: 'Order Size', variants: [
            { value: 'avgOrderSize', label: 'Avg.' },
            { value: 'medianOrderSize', label: 'Median' },
        ]
    },
    {
        label: 'Gross Margin', vendorLabel: 'Discount', variants: [
            { value: 'avgGrossMarginPct', label: 'Avg.' },
            { value: 'medianGrossMarginPct', label: 'Median' },
        ]
    },
    {
        label: 'Gross Margin (w/o PM)', vendorLabel: 'Discount (w/o PM)', variants: [
            { value: 'avgGrossMarginPctWoPM', label: 'Avg.' },
            { value: 'medianGrossMarginPctWoPM', label: 'Median' },
        ]
    },
    {
        label: 'Missed Opportunity', variants: [
            { value: 'missedOpportunity', label: 'Total' },
            { value: 'avgMissedOpportunity', label: 'Avg.' },
            { value: 'medianMissedOpportunity', label: 'Median' },
        ]
    },
    {
        label: 'Part Value', variants: [
            { value: 'avgPartValue', label: 'Avg.' },
            { value: 'medianPartValue', label: 'Median' },
        ]
    },
    {
        label: 'ETA Change', variants: [
            { value: 'avgEtaChangeDays', label: 'Avg.' },
            { value: 'medianEtaChangeDays', label: 'Median' },
        ]
    },
    {
        label: 'Cost Variance', variants: [
            { value: 'totalCostVariance', label: 'Total' },
            { value: 'avgCostVariance', label: 'Avg.' },
            { value: 'medianCostVariance', label: 'Median' },
        ]
    },
    {
        label: 'Delivery Variance', variants: [
            { value: 'avgDeliveryVarianceDays', label: 'Avg.' },
            { value: 'medianDeliveryVarianceDays', label: 'Median' },
        ]
    },
    {
        label: 'List Price', variants: [
            { value: 'avgListPrice', label: 'Avg.' },
            { value: 'medianListPrice', label: 'Median' },
        ]
    },
    {
        label: '% OEM', variants: [
            { value: 'pctOemVolume', label: 'Volume' },
            { value: 'pctOemCount', label: 'Count' },
        ]
    },
    {
        label: '% LKQ', variants: [
            { value: 'pctLkqVolume', label: 'Volume' },
            { value: 'pctLkqCount', label: 'Count' },
        ]
    },
    {
        label: '% Aftermarket', variants: [
            { value: 'pctAftermarketVolume', label: 'Volume' },
            { value: 'pctAftermarketCount', label: 'Count' },
        ]
    },
];

function MetricPopoverSelect({ value, options, onChange, width = '220px', domain }: {
    value: string;
    options: Array<{ value: string; label: string; tooltip?: string; }>;
    onChange: (value: string) => void;
    width?: string;
    domain?: string;
}) {
    const [isOpen, setIsOpen] = useState(false);
    const [search, setSearch] = useState('');
    const triggerRef = useRef<HTMLButtonElement>(null);
    const [popW, setPopW] = useState<number>(0);

    useEffect(() => {
        if (isOpen && triggerRef.current) setPopW(triggerRef.current.offsetWidth);
    }, [isOpen]);
    useEffect(() => { if (!isOpen) setSearch(''); }, [isOpen]);

    const isVendorDomain = domain === 'vendor';

    const allValues = new Set(options.map(m => m.value));
    const validGroups = RANKING_VARIANT_GROUPS.reduce<{ group: VariantGroup; representative: string; members: Set<string>; }[]>((acc, group) => {
        const available = group.variants.filter(v => allValues.has(v.value));
        if (available.length >= 2) {
            acc.push({ group: { ...group, variants: available }, representative: available[0].value, members: new Set(available.map(v => v.value)) });
        }
        return acc;
    }, []);
    const metricToGroup = new Map<string, typeof validGroups[0]>();
    validGroups.forEach(vg => { vg.members.forEach(m => metricToGroup.set(m, vg)); });

    const condensed: { value: string; label: string; }[] = [];
    const seenReps = new Set<string>();
    for (const opt of options) {
        const vg = metricToGroup.get(opt.value);
        if (vg) {
            if (!seenReps.has(vg.representative)) {
                seenReps.add(vg.representative);
                const label = isVendorDomain && vg.group.vendorLabel ? vg.group.vendorLabel : vg.group.label;
                condensed.push({ value: vg.representative, label });
            }
        } else {
            condensed.push({ value: opt.value, label: opt.label });
        }
    }

    const currentVg = metricToGroup.get(value);
    const dropdownValue = currentVg ? currentVg.representative : value;
    const selectedLabel = condensed.find(o => o.value === dropdownValue)?.label ?? 'Select Metric';

    const filtered = search
        ? condensed.filter(o => o.label?.toLowerCase().includes(search.toLowerCase()))
        : condensed;

    return (
        <Popover placement="bottom-start" isOpen={isOpen} onOpen={() => setIsOpen(true)} onClose={() => setIsOpen(false)} matchWidth>
            <PopoverTrigger>
                <Button
                    ref={triggerRef}
                    variant="outline"
                    w={width}
                    h="36px"
                    bg="white"
                    borderColor="gray.300"
                    borderRadius="md"
                    fontWeight="normal"
                    textAlign="left"
                    justifyContent="space-between"
                    px={3}
                    _hover={{ borderColor: 'gray.400' }}
                    rightIcon={<Icon as={TbChevronDown as React.ElementType} boxSize={4} color="gray.500" />}
                >
                    <Text noOfLines={1} fontSize="sm" color="gray.800">{selectedLabel}</Text>
                </Button>
            </PopoverTrigger>
            <Portal>
                <PopoverContent w={popW ? `${popW}px` : width} maxH="320px" overflowY="auto" zIndex="popover" onClick={e => e.stopPropagation()}>
                    <PopoverBody p={2}>
                        <VStack align="stretch" spacing={1}>
                            <InputGroup size="sm" mb={1}>
                                <InputLeftElement pointerEvents="none">
                                    <Icon as={TbSearch as React.ElementType} color="gray.400" boxSize={3} />
                                </InputLeftElement>
                                <Input placeholder="Search…" value={search} onChange={e => setSearch(e.target.value)} bg="white" borderColor="gray.200" />
                            </InputGroup>
                            {filtered.map(opt => {
                                const active = opt.value === dropdownValue;
                                return (
                                    <Flex
                                        key={opt.value}
                                        align="center"
                                        px={2}
                                        py={1.5}
                                        borderRadius="md"
                                        cursor="pointer"
                                        bg={active ? '#E6F1FD' : 'transparent'}
                                        _hover={{ bg: active ? '#E6F1FD' : 'gray.50' }}
                                        onClick={() => {
                                            onChange(opt.value);
                                            setIsOpen(false);
                                        }}
                                    >
                                        <Text flex={1} fontSize="sm" color={active ? '#0071E3' : 'gray.700'} fontWeight={active ? 'medium' : 'normal'}>
                                            {opt.label}
                                        </Text>
                                        {active && <Icon as={TbCheck as React.ElementType} boxSize={4} color="#0071E3" />}
                                    </Flex>
                                );
                            })}
                            {filtered.length === 0 && <Text color="gray.500" textAlign="center" py={2} fontSize="sm">No matches</Text>}
                        </VStack>
                    </PopoverBody>
                </PopoverContent>
            </Portal>
        </Popover>
    );
}

type ColumnKey = string;
type SortableColumn = ColumnKey | 'name';

interface DomainRankingSectionProps {
    domain: DomainType;
    items: DomainStatItem[];
    totals: DomainStatsTotals;
    isLoading: boolean;
    isFetching?: boolean;
    domainLabel: string;
    columns?: ColumnKey[];
    sortBy: string;
    sortOrder: 'asc' | 'desc';
    onSortChange: (field: string) => void;
    defaultMetric?: ChartMetricType;
    maxChartItems?: number;
    height?: number;
    comparison?: ReportingComparison;
    preset?: string;
    /** Enables fullscreen + resizable columns + column prefs + density/dividers (table view only) */
    tableExperienceKey?: string;
    /** Vendor tab: group rows by supplier or preference tier. */
    vendorGroupBy?: VendorGroupBy;
    onVendorGroupByChange?: (value: VendorGroupBy) => void;
    /** Prefetch stats for the alternate group-by on hover (avoids skeleton on first switch). */
    onPrefetchVendorGroupBy?: (groupBy: VendorGroupBy) => void;
}

// Per-tab view selections persisted to localStorage (explicit save). Column
// visibility/width/density are persisted separately by their own hooks.
interface PersistedDomainView {
    viewType: ViewType;
    chartMetric: ChartMetricType;
    showComparison: boolean;
    chartSortOrder: 'asc' | 'desc';
    fixedView: boolean;
    marketplaceView: boolean;
    gmMetric: 'avg' | 'median';
    missedOppMetric: 'avg' | 'median';
    pctTotalMetric: 'volume' | 'count' | 'profit';
    orderSizeMetric: 'avg' | 'median';
    discountMetric: 'avg' | 'median';
    discountWoPMMetric: 'avg' | 'median';
    gmWoPMMetric: 'avg' | 'median';
    partValueMetric: 'avg' | 'median';
    etaChangeMetric: 'avg' | 'median';
    pctOemMetric: 'volume' | 'count';
    pctLkqMetric: 'volume' | 'count';
    pctAftermarketMetric: 'volume' | 'count';
    tableSortBy: SortableColumn | null;
    tableSortOrder: 'asc' | 'desc';
    vendorGroupBy?: VendorGroupBy;
}

const DomainRankingSection = ({
    domain,
    items,
    totals,
    isLoading,
    isFetching,
    domainLabel,
    columns,
    sortBy,
    sortOrder,
    onSortChange,
    defaultMetric = 'orderVolume',
    maxChartItems = 12,
    height = 500,
    comparison,
    preset,
    tableExperienceKey,
    vendorGroupBy: vendorGroupByProp,
    onVendorGroupByChange,
    onPrefetchVendorGroupBy,
}: DomainRankingSectionProps) => {
    const sessionContext = useContext(SessionContext);
    // Only hide after session resolves with invoicing off (avoid clearing saved prefs during auth load).
    const invoicingEnabled = orgShowsInvoicingReportingMetrics(sessionContext);
    const columnOptions = useMemo(() => ({ invoicingEnabled }), [invoicingEnabled]);
    const effectiveVendorGroupBy: VendorGroupBy = vendorGroupByProp ?? 'vendor';
    const viewPrefsKey = tableExperienceKey ? `${tableExperienceKey}_view` : '';
    // Last-saved selections (read once on mount); used to seed initial state.
    const savedView = useMemo(
        () => loadViewPrefs<Partial<PersistedDomainView>>(viewPrefsKey, {}),
        [viewPrefsKey]
    );

    const [viewType, setViewType] = useState<ViewType>(savedView.viewType ?? 'graph');
    const [isFullScreen, setIsFullScreen] = useState(false);
    const [chartMetric, setChartMetric] = useState<ChartMetricType>(savedView.chartMetric ?? defaultMetric);
    const settingsDrawerRaw = useDisclosure();
    const [colDragKey, setColDragKey] = useState<string | null>(null);

    // Toggle states for location columns
    const [gmMetric, setGmMetric] = useState<'avg' | 'median'>(savedView.gmMetric ?? 'avg');
    const [missedOppMetric, setMissedOppMetric] = useState<'avg' | 'median'>(savedView.missedOppMetric ?? 'avg');
    const [pctTotalMetric, setPctTotalMetric] = useState<'volume' | 'count' | 'profit'>(savedView.pctTotalMetric ?? 'volume');

    // Toggle states for vendor columns
    const [orderSizeMetric, setOrderSizeMetric] = useState<'avg' | 'median'>(savedView.orderSizeMetric ?? 'avg');
    const [discountMetric, setDiscountMetric] = useState<'avg' | 'median'>(savedView.discountMetric ?? 'avg');
    const [discountWoPMMetric, setDiscountWoPMMetric] = useState<'avg' | 'median'>(savedView.discountWoPMMetric ?? 'avg');
    const [gmWoPMMetric, setGmWoPMMetric] = useState<'avg' | 'median'>(savedView.gmWoPMMetric ?? 'avg');

    // Toggle states for location-only columns
    const [partValueMetric, setPartValueMetric] = useState<'avg' | 'median'>(savedView.partValueMetric ?? 'avg');
    // ETA change toggle (vendor): avg vs median days
    const [etaChangeMetric, setEtaChangeMetric] = useState<'avg' | 'median'>(savedView.etaChangeMetric ?? 'avg');
    const [pctOemMetric, setPctOemMetric] = useState<'volume' | 'count'>(savedView.pctOemMetric ?? 'volume');
    const [pctLkqMetric, setPctLkqMetric] = useState<'volume' | 'count'>(savedView.pctLkqMetric ?? 'volume');
    const [pctAftermarketMetric, setPctAftermarketMetric] = useState<'volume' | 'count'>(savedView.pctAftermarketMetric ?? 'volume');

    const [showComparison, setShowComparison] = useState<boolean>(savedView.showComparison ?? true);
    const [isOtherExpanded, setIsOtherExpanded] = useState<boolean>(false);

    const comparisonSupported = isReportingComparisonSupported(preset);

    useEffect(() => {
        if (!comparisonSupported) {
            setShowComparison(false);
        }
    }, [comparisonSupported]);

    // Marketplace breakdown (location/vendor/oem): clusters the chart by source
    // and expands each table row into per-marketplace rows.
    const [marketplaceView, setMarketplaceView] = useState<boolean>(savedView.marketplaceView ?? false);
    const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());

    // Fixed view toggle - when ON, keeps same entities in same order when switching metrics
    const [fixedView, setFixedView] = useState<boolean>(savedView.fixedView ?? false);

    const [chartSortOrder, setChartSortOrder] = useState<'asc' | 'desc'>(savedView.chartSortOrder ?? 'desc');
    const [tableSearchQuery, setTableSearchQuery] = useState('');

    // Local table sorting state (client-side, no network request)
    // null means no active sort (use original order from API)
    const [tableSortBy, setTableSortBy] = useState<SortableColumn | null>(
        savedView.tableSortBy == null || savedView.tableSortBy === 'name'
            ? (savedView.tableSortBy ?? null)
            : migrateLegacyColumnKey(savedView.tableSortBy, savedView)
    );
    const [tableSortOrder, setTableSortOrder] = useState<'asc' | 'desc'>(savedView.tableSortOrder ?? 'asc');

    // Always-current snapshot of persistable selections, so save/discard handlers
    // never close over stale state.
    const liveViewRef = useRef<PersistedDomainView>();
    liveViewRef.current = {
        viewType, chartMetric, showComparison, chartSortOrder, fixedView, marketplaceView,
        gmMetric, missedOppMetric, pctTotalMetric, orderSizeMetric, discountMetric,
        discountWoPMMetric, gmWoPMMetric, partValueMetric, etaChangeMetric,
        pctOemMetric, pctLkqMetric, pctAftermarketMetric, tableSortBy, tableSortOrder,
        vendorGroupBy: effectiveVendorGroupBy,
    };
    const drawerSnapshotRef = useRef<PersistedDomainView | null>(null);
    const tableUiSnapshotRef = useRef<TableSettings | null>(null);
    const columnPrefsSnapshotRef = useRef<ColumnPrefs | null>(null);

    const applyView = useCallback((v: PersistedDomainView) => {
        setViewType(v.viewType);
        setChartMetric(v.chartMetric);
        setShowComparison(v.showComparison);
        setChartSortOrder(v.chartSortOrder);
        setFixedView(v.fixedView);
        setMarketplaceView(v.marketplaceView);
        setGmMetric(v.gmMetric);
        setMissedOppMetric(v.missedOppMetric);
        setPctTotalMetric(v.pctTotalMetric);
        setOrderSizeMetric(v.orderSizeMetric);
        setDiscountMetric(v.discountMetric);
        setDiscountWoPMMetric(v.discountWoPMMetric);
        setGmWoPMMetric(v.gmWoPMMetric);
        setPartValueMetric(v.partValueMetric);
        setEtaChangeMetric(v.etaChangeMetric);
        setPctOemMetric(v.pctOemMetric);
        setPctLkqMetric(v.pctLkqMetric);
        setPctAftermarketMetric(v.pctAftermarketMetric);
        setTableSortBy(
            v.tableSortBy == null || v.tableSortBy === 'name'
                ? (v.tableSortBy ?? null)
                : migrateLegacyColumnKey(v.tableSortBy, v)
        );
        setTableSortOrder(v.tableSortOrder);
        if (v.vendorGroupBy) onVendorGroupByChange?.(v.vendorGroupBy);
    }, [onVendorGroupByChange]);

    const effectiveShowPrior = showComparison && comparisonSupported;
    const effectiveComparison = effectiveShowPrior ? comparison : undefined;
    const showPrior = effectiveShowPrior;

    // Variant group logic for condensed metric selection
    const metricOptions = useMemo(() => {
        let options = domain === 'vendor'
            ? VENDOR_CHART_METRIC_OPTIONS
            : domain === 'location'
                ? LOCATION_CHART_METRIC_OPTIONS
                : domain === 'insurance'
                    ? INSURANCE_CHART_METRIC_OPTIONS
                    : domain === 'oem'
                        ? OEM_CHART_METRIC_OPTIONS
                        : CHART_METRIC_OPTIONS;
        options = filterChartMetricsForInvoicing(options, invoicingEnabled);
        if (domain === 'vendor' && effectiveVendorGroupBy === 'preference') {
            options = options.filter((o) => o.value !== 'quoteRate');
        }
        return options;
    }, [domain, effectiveVendorGroupBy, invoicingEnabled]);

    useEffect(() => {
        if (!invoicingEnabled && isInvoicingGatedChartMetric(chartMetric)) {
            setChartMetric(defaultMetric);
        }
    }, [invoicingEnabled, chartMetric, defaultMetric]);

    const activeVariantGroup = useMemo(() => {
        const allValues = new Set<string>(metricOptions.map(m => m.value));
        for (const group of RANKING_VARIANT_GROUPS) {
            const available = group.variants.filter(v => allValues.has(v.value));
            if (available.length >= 2) {
                const members = new Set(available.map(v => v.value));
                if (members.has(chartMetric)) {
                    return { ...group, variants: available };
                }
            }
        }
        return null;
    }, [chartMetric, metricOptions]);

    const handleCondensedMetricChange = useCallback((selectedValue: string) => {
        const allValues = new Set<string>(metricOptions.map(m => m.value));
        for (const group of RANKING_VARIANT_GROUPS) {
            const available = group.variants.filter(v => allValues.has(v.value));
            if (available.length >= 2 && available[0].value === selectedValue) {
                setChartMetric(available[0].value as ChartMetricType);
                return;
            }
        }
        setChartMetric(selectedValue as ChartMetricType);
    }, [metricOptions]);

    // Handler for metric change that updates local state
    const handleMetricChange = (newMetric: ChartMetricType) => {
        setChartMetric(newMetric);
    };

    // Use domain-specific leaf columns (aggregations are real columns, not header toggles)
    const displayColumns = useMemo(() => {
        const leaves = columns
            ? columns.flatMap((key) => {
                const group = getGroupForKey(key);
                if (group && group.legacyToggleKey === key) {
                    return group.children.map((c) => c.key);
                }
                return [key];
            })
            : getLeafKeysForDomain(domain, effectiveVendorGroupBy, columnOptions);
        if (invoicingEnabled) return leaves;
        return leaves.filter((k) => !INVOICING_GATED_RANKING_LEAF_KEYS.has(k));
    }, [columns, domain, effectiveVendorGroupBy, columnOptions, invoicingEnabled]);

    const effectiveDisplayColumns = displayColumns;

    const defaultHidden = useMemo(
        () => getDefaultHiddenLeafKeys(domain, effectiveVendorGroupBy, columnOptions),
        [domain, effectiveVendorGroupBy, columnOptions]
    );

    const prefsStorageKey = tableExperienceKey ? `${tableExperienceKey}_cols` : '';
    const uiSettingsKey = tableExperienceKey ? `${tableExperienceKey}_ui` : '';
    migrateColumnPrefsInStorage(
        prefsStorageKey || `reporting_domain_inactive_cols_${domain}`,
        domain,
        savedView,
        effectiveVendorGroupBy,
        columnOptions,
    );
    const { settings: tableUiSettings, updateSettings: updateTableUi, tableSettingsSx } = useTableSettings(
        uiSettingsKey || 'reporting_domain_inactive'
    );
    const { widths: colWidths, updateWidth, resetWidths, hasCustomWidths } = useColumnWidths(
        tableExperienceKey || `reporting_domain_inactive_${domain}`
    );
    const columnPrefs = useReportingColumnPrefs(
        prefsStorageKey || `reporting_domain_inactive_cols_${domain}`,
        effectiveDisplayColumns,
        [],
        defaultHidden
    );

    const tableColumns = useMemo((): ColumnKey[] => {
        if (!tableExperienceKey) return effectiveDisplayColumns;
        const vis = columnPrefs.visibleOrderedKeys.filter((k) =>
            effectiveDisplayColumns.includes(k)
        );
        return vis.length > 0 ? vis : effectiveDisplayColumns;
    }, [tableExperienceKey, effectiveDisplayColumns, columnPrefs.visibleOrderedKeys]);

    const isAgreedDiscountColumnVisible = useMemo(
        () => viewType === 'table' && tableColumns.includes('agreedDiscount'),
        [viewType, tableColumns]
    );

    const hideAgreedDiscountColumn = useCallback(() => {
        if (!tableExperienceKey) return;
        if (!columnPrefs.prefs.hidden.includes('agreedDiscount')) {
            columnPrefs.setPrefs({
                ...columnPrefs.prefs,
                hidden: [...columnPrefs.prefs.hidden, 'agreedDiscount'],
            });
        }
        setDraftColumnRows((prev) =>
            prev.length > 0
                ? prev.map((r) => (r.key === 'agreedDiscount' ? { ...r, visible: false } : r))
                : prev
        );
    }, [tableExperienceKey, columnPrefs]);

    const handleVendorGroupByChange = useCallback((next: VendorGroupBy) => {
        if (next === 'preference') {
            hideAgreedDiscountColumn();
            setMarketplaceView(false);
            if (chartMetric === 'quoteRate') {
                setChartMetric('orderVolume');
            }
        }
        onVendorGroupByChange?.(next);
        if (viewPrefsKey) {
            const current = loadViewPrefs<Partial<PersistedDomainView>>(viewPrefsKey, {});
            saveViewPrefs(viewPrefsKey, { ...current, vendorGroupBy: next });
        }
    }, [hideAgreedDiscountColumn, onVendorGroupByChange, viewPrefsKey, chartMetric]);

    const showVendorGroupByControl = domain === 'vendor';

    const renderVendorGroupByControl = () => (
        <HStack spacing={0}>
            {([
                { value: 'vendor' as VendorGroupBy, label: 'Total' },
                { value: 'preference' as VendorGroupBy, label: 'By Preference' },
            ]).map((opt, i, arr) => {
                const isActive = effectiveVendorGroupBy === opt.value;
                const nextIsActive = i < arr.length - 1 && arr[i + 1].value === effectiveVendorGroupBy;
                return (
                    <Button
                        key={opt.value}
                        size="sm"
                        h="32px"
                        flex={1}
                        borderRadius={0}
                        borderLeftRadius={i === 0 ? 'md' : 0}
                        borderRightRadius={i === arr.length - 1 ? 'md' : 0}
                        border="1px solid"
                        borderColor={isActive ? '#0071E3' : 'gray.200'}
                        borderLeftColor={i > 0 ? (isActive ? '#0071E3' : 'transparent') : undefined}
                        borderRightColor={nextIsActive ? 'transparent' : undefined}
                        ml={i > 0 ? '-1px' : undefined}
                        zIndex={isActive ? 1 : 0}
                        bg={isActive ? '#E6F1FD' : 'white'}
                        color={isActive ? '#0071E3' : 'gray.600'}
                        fontWeight={isActive ? 'semibold' : 'normal'}
                        fontSize="sm"
                        _hover={{ bg: isActive ? '#E6F1FD' : 'gray.50' }}
                        onMouseEnter={() => {
                            if (!isActive) onPrefetchVendorGroupBy?.(opt.value);
                        }}
                        onClick={() => handleVendorGroupByChange(opt.value)}
                    >
                        {opt.label}
                    </Button>
                );
            })}
        </HStack>
    );

    useEffect(() => {
        if (isAgreedDiscountColumnVisible && effectiveVendorGroupBy !== 'vendor') {
            handleVendorGroupByChange('vendor');
        }
    }, [isAgreedDiscountColumnVisible, effectiveVendorGroupBy, handleVendorGroupByChange]);

    const columnDrawerRows = useMemo((): ColumnPickerRow[] => {
        if (!tableExperienceKey) return [];
        return prefsToPickerRows(columnPrefs.prefs, domain, effectiveVendorGroupBy, columnOptions);
    }, [tableExperienceKey, columnPrefs.prefs, domain, effectiveVendorGroupBy, columnOptions]);

    const [draftTableUi, setDraftTableUi] = useState<TableSettings>(tableUiSettings);
    const [draftColumnRows, setDraftColumnRows] = useState<ColumnPickerRow[]>([]);
    const [expandedColumnGroups, setExpandedColumnGroups] = useState<Set<string>>(new Set());
    const [pendingResetWidths, setPendingResetWidths] = useState(false);
    const [pendingResetColumns, setPendingResetColumns] = useState(false);

    const openSettingsDrawer = useCallback(() => {
        drawerSnapshotRef.current = liveViewRef.current ?? null;
        tableUiSnapshotRef.current = tableUiSettings;
        columnPrefsSnapshotRef.current = columnPrefs.prefs;
        setDraftTableUi(tableUiSettings);
        setDraftColumnRows(columnDrawerRows.map((r) => ({
            ...r,
            children: r.children?.map((c) => ({ ...c })),
        })));
        setExpandedColumnGroups(new Set());
        setPendingResetWidths(false);
        setPendingResetColumns(false);
        settingsDrawerRaw.onOpen();
    }, [tableUiSettings, columnDrawerRows, columnPrefs.prefs, settingsDrawerRaw]);

    const handleDrawerSave = useCallback(() => {
        if (liveViewRef.current) saveViewPrefs(viewPrefsKey, liveViewRef.current);
        updateTableUi(draftTableUi);
        if (draftColumnRows.length > 0) {
            const nextPrefs = pickerRowsToPrefs(draftColumnRows, domain, effectiveVendorGroupBy, columnOptions);
            columnPrefs.setPrefs(nextPrefs);
            const agreedVisible =
                nextPrefs.order.includes('agreedDiscount') &&
                !nextPrefs.hidden.includes('agreedDiscount');
            if (agreedVisible && effectiveVendorGroupBy !== 'vendor') {
                handleVendorGroupByChange('vendor');
            }
        }
        if (pendingResetWidths) {
            resetWidths();
        }
        drawerSnapshotRef.current = null;
        tableUiSnapshotRef.current = null;
        columnPrefsSnapshotRef.current = null;
        setPendingResetColumns(false);
        settingsDrawerRaw.onClose();
    }, [viewPrefsKey, draftTableUi, draftColumnRows, pendingResetWidths, columnPrefs, updateTableUi, resetWidths, settingsDrawerRaw, effectiveVendorGroupBy, handleVendorGroupByChange, domain, columnOptions]);

    const handleDrawerDiscard = useCallback(() => {
        const snap = drawerSnapshotRef.current;
        if (snap) applyView(snap);
        const uiSnap = tableUiSnapshotRef.current;
        if (uiSnap) updateTableUi(uiSnap);
        const colSnap = columnPrefsSnapshotRef.current;
        if (colSnap) columnPrefs.setPrefs(colSnap);
        drawerSnapshotRef.current = null;
        tableUiSnapshotRef.current = null;
        columnPrefsSnapshotRef.current = null;
        settingsDrawerRaw.onClose();
    }, [applyView, updateTableUi, columnPrefs, settingsDrawerRaw]);

    const handleDraftColumnToggle = useCallback((key: string) => {
        setPendingResetColumns(false);
        setDraftColumnRows((prev) => {
            const parent = prev.find((r) => r.key === key);
            const enablingParent = parent && !parent.visible;
            if (key === 'agreedDiscount' && enablingParent && effectiveVendorGroupBy !== 'vendor') {
                handleVendorGroupByChange('vendor');
            }
            return togglePickerRowForDomain(prev, key, domain);
        });
    }, [effectiveVendorGroupBy, handleVendorGroupByChange, domain]);

    const handleResetColumnsToDefault = useCallback(() => {
        setDraftColumnRows(defaultPickerRows(domain, effectiveVendorGroupBy, columnOptions));
        setExpandedColumnGroups(new Set());
        setPendingResetColumns(true);
    }, [domain, effectiveVendorGroupBy, columnOptions]);

    const handleDraftColumnReorder = useCallback((fromKey: string, toKey: string) => {
        setPendingResetColumns(false);
        setDraftColumnRows((prev) => reorderPickerRows(prev, fromKey, toKey));
    }, []);

    // Get available metrics for the chart selector based on domain
    const isVendor = domain === 'vendor';
    const isLocation = domain === 'location';
    const isInsurance = domain === 'insurance';
    const isOEM = domain === 'oem';

    // ---- Marketplace breakdown eligibility ----------------------------------
    const supportsMarketplace = isLocation || isVendor || isOEM; // not insurance
    const hasSourceData = useMemo(
        () => items.some((it) => it.bySource && Object.keys(it.bySource).length > 0),
        [items]
    );
    const chartMetricEligibleForMarketplace = !MARKETPLACE_EXCLUDED_METRICS.has(chartMetric);
    const canShowMarketplaceToggle = supportsMarketplace && hasSourceData;
    // Active in the chart only when the selected metric is eligible; the table
    // can always expand (per-column values come from each marketplace).
    const marketplaceChartActive = marketplaceView && canShowMarketplaceToggle && chartMetricEligibleForMarketplace;
    const marketplaceTableActive = marketplaceView && canShowMarketplaceToggle;
    const marketplaceDisabledForChart = viewType === 'graph' && !chartMetricEligibleForMarketplace;

    const toggleExpandedRow = useCallback((key: string) => {
        setExpandedRows((prev) => {
            const next = new Set(prev);
            if (next.has(key)) next.delete(key);
            else next.add(key);
            return next;
        });
    }, []);

    // Sources for a row, ordered by the active table sort field (descending).
    // Include prior so prior-only marketplaces (current 0) still appear when expanded.
    const getRowSources = useCallback((item: DomainStatItem): string[] => {
        const bs = item.bySource;
        if (!bs) return [];
        const field = sortBy || 'orderVolume';
        return Object.keys(bs).sort((a, b) => {
            const av = (Number((bs[a] as any)?.[field]) || 0)
                + (Number((bs[a] as any)?.prior?.[field]) || 0);
            const bv = (Number((bs[b] as any)?.[field]) || 0)
                + (Number((bs[b] as any)?.prior?.[field]) || 0);
            return bv - av;
        });
    }, [sortBy]);

    // Get label for current metric for the title
    const currentMetricOption = metricOptions.find(o => o.value === chartMetric);
    const metricLabel = currentMetricOption?.label || '';

    // Deduplicate items by name (in case backend returns duplicates)
    const deduplicatedItems = useMemo(() => {
        if (!items || items.length === 0) return [];

        const seen = new Map<string, DomainStatItem>();
        items.forEach(item => {
            const key = (effectiveVendorGroupBy === 'preference'
                ? item.id
                : item.name)?.toLowerCase() || item.id || '';
            if (!seen.has(key)) {
                seen.set(key, item);
            }
        });
        return Array.from(seen.values());
    }, [items, effectiveVendorGroupBy]);

    // Base order items - sorted by default metric (orderVolume) descending, used for fixed view
    const baseOrderItems = useMemo(() => {
        if (deduplicatedItems.length === 0) return [];

        return [...deduplicatedItems].sort((a, b) =>
            compareMetricWithPriorTiebreaker(a, b, 'orderVolume', 'desc', effectiveShowPrior),
        );
    }, [deduplicatedItems, effectiveShowPrior]);

    // Client-side sorted items for chart view
    const sortedChartItems = useMemo(() => {
        if (deduplicatedItems.length === 0) return [];

        // When fixedView is ON, use the base order (sorted by orderVolume)
        // This keeps the same entities in the same positions when switching metrics
        if (fixedView) {
            return baseOrderItems;
        }

        // When fixedView is OFF, sort by the currently selected metric using chartSortOrder
        return [...deduplicatedItems].sort((a, b) =>
            compareMetricWithPriorTiebreaker(
                a,
                b,
                chartMetric as string,
                chartSortOrder,
                effectiveShowPrior,
            ),
        );
    }, [deduplicatedItems, chartMetric, fixedView, baseOrderItems, chartSortOrder, effectiveShowPrior]);

    // Helper to get value for dynamic columns
    const getValue = useCallback((item: DomainStatItem | DomainStatsTotals, col: ColumnKey) => {
        return getTableCellValue(item, col, totals);
    }, [totals]);

    // Client-side sorted items for table view
    const sortedItems = useMemo(() => {
        if (deduplicatedItems.length === 0) return [];

        // If no sort is active, return items in original order
        if (tableSortBy === null) return deduplicatedItems;

        return [...deduplicatedItems].sort((a, b) => {
            let aVal: number | string;
            let bVal: number | string;

            if (tableSortBy === 'name') {
                aVal = a.name?.toLowerCase() || '';
                bVal = b.name?.toLowerCase() || '';
            } else {
                aVal = getValue(a, tableSortBy as ColumnKey) || 0;
                bVal = getValue(b, tableSortBy as ColumnKey) || 0;
            }

            if (typeof aVal === 'string' && typeof bVal === 'string') {
                return tableSortOrder === 'asc'
                    ? aVal.localeCompare(bVal)
                    : bVal.localeCompare(aVal);
            }

            return tableSortOrder === 'asc'
                ? (aVal as number) - (bVal as number)
                : (bVal as number) - (aVal as number);
        });
    }, [deduplicatedItems, tableSortBy, tableSortOrder, getValue]);

    // For vendor and insurance domains: limit to top 25 and create "Other" row for remaining
    const TOP_N_ITEMS = 25;

    const { displayedItems, otherRow, otherItems } = useMemo(() => {
        const shouldLimitToTopN =
            (domain === 'vendor' && effectiveVendorGroupBy === 'vendor') || domain === 'insurance';
        if (!shouldLimitToTopN || sortedItems.length <= TOP_N_ITEMS) {
            return { displayedItems: sortedItems, otherRow: null, otherItems: [] as DomainStatItem[] };
        }

        const top25 = sortedItems.slice(0, TOP_N_ITEMS);
        const remaining = sortedItems.slice(TOP_N_ITEMS);

        // Sum up the remaining vendors into an "Other" row
        const otherData: Partial<DomainStatItem> = {
            id: 'other',
            name: 'Other',
            orderCount: 0,
            orderVolume: 0,
            partsOrdered: 0,
            grossProfit: 0,
            missedOpportunity: 0,
            avgListPrice: 0,
            avgListPriceWoPM: 0,
            avgOrderSize: 0,
            avgGrossMarginPct: 0,
            avgGrossMarginPctWoPM: 0,
            medianListPrice: 0,
            medianListPriceWoPM: 0,
            medianOrderSize: 0,
            medianGrossMarginPct: 0,
            medianGrossMarginPctWoPM: 0,
            grossMarginLossFromPM: 0,
            moEligibleOrderCount: 0,
            avgMissedOpportunity: 0,
            medianMissedOpportunity: 0,
            partListPricePctOfOE: 0,
            pctAutoOrdered: 0,
            pctAutoOrderEnabled: 0,
            pctPartsFlagged: 0,
            avgPartValue: 0,
            medianPartValue: 0,
            totalQuotes: 0,
            avgQuotesPerOrder: 0,
            totalCostVariance: 0,
            avgCostVariance: 0,
            medianCostVariance: 0,
            avgDeliveryVarianceDays: 0,
            medianDeliveryVarianceDays: 0,
            deliveryVariancePartCount: 0,
            pctPartsInvoiced: 0,
            quoteOpportunities: 0,
            quotesSubmitted: 0,
            totalReturnCount: 0,
            totalReturnValue: 0,
            totalReturnPct: 0,
            pctOemVolume: 0,
            pctOemCount: 0,
            pctLkqVolume: 0,
            pctLkqCount: 0,
            pctAftermarketVolume: 0,
            pctAftermarketCount: 0,
        };

        // Sum numeric fields
        remaining.forEach(item => {
            otherData.orderCount! += item.orderCount || 0;
            otherData.orderVolume! += item.orderVolume || 0;
            otherData.partsOrdered! += item.partsOrdered || 0;
            otherData.grossProfit! += item.grossProfit || 0;
            otherData.missedOpportunity! += item.missedOpportunity || 0;
            otherData.moEligibleOrderCount! += item.moEligibleOrderCount ?? item.orderCount ?? 0;
            otherData.totalQuotes! += item.totalQuotes || 0;
            otherData.totalReturnCount! += item.totalReturnCount || 0;
            otherData.totalReturnValue! += item.totalReturnValue || 0;
            otherData.totalCostVariance! += item.totalCostVariance || 0;
            otherData.deliveryVariancePartCount! += item.deliveryVariancePartCount || 0;
            otherData.quoteOpportunities! += item.quoteOpportunities || 0;
            otherData.quotesSubmitted! += item.quotesSubmitted || 0;
        });

        // Calculate weighted averages for percentage/average metrics
        const totalVolume = otherData.orderVolume || 1;
        const totalOrders = otherData.orderCount || 1;

        otherData.avgQuotesPerOrder = totalOrders > 0 ? (otherData.totalQuotes || 0) / totalOrders : 0;
        otherData.totalReturnPct = (otherData.partsOrdered || 0) > 0
            ? ((otherData.totalReturnCount || 0) / otherData.partsOrdered!) * 100
            : 0;
        otherData.quoteRate = (otherData.quoteOpportunities || 0) > 0
            ? ((otherData.quotesSubmitted || 0) / otherData.quoteOpportunities!) * 100
            : undefined;

        let weightedAvgGM = 0;
        let weightedAvgGMWoPM = 0;
        let weightedAvgOrderSize = 0;
        let costVarWeight = 0;
        let deliveryWeight = 0;
        let weightedAvgDelivery = 0;
        let invoicedPartsWeight = 0;
        let weightedPctInvoiced = 0;

        remaining.forEach(item => {
            const volumeWeight = (item.orderVolume || 0) / totalVolume;
            weightedAvgGM += (item.avgGrossMarginPct || 0) * volumeWeight;
            weightedAvgGMWoPM += (item.avgGrossMarginPctWoPM || 0) * volumeWeight;
            // avgCostVariance is per matched part; pctPartsInvoiced is over ordered parts.
            const orderedParts = item.partsOrdered || 0;
            const matchedParts = orderedParts * ((item.pctPartsInvoiced || 0) / 100);
            if (matchedParts > 0) {
                costVarWeight += matchedParts;
            }
            if (orderedParts > 0) {
                invoicedPartsWeight += orderedParts;
                weightedPctInvoiced += (item.pctPartsInvoiced || 0) * orderedParts;
            }
            const delivParts = item.deliveryVariancePartCount || 0;
            if (delivParts > 0) {
                deliveryWeight += delivParts;
                weightedAvgDelivery += (item.avgDeliveryVarianceDays || 0) * delivParts;
            }
        });

        // Average order size = total volume / total orders
        weightedAvgOrderSize = totalOrders > 0 ? totalVolume / totalOrders : 0;

        otherData.avgGrossMarginPct = weightedAvgGM;
        otherData.avgGrossMarginPctWoPM = weightedAvgGMWoPM;
        otherData.avgOrderSize = weightedAvgOrderSize;
        otherData.avgCostVariance = costVarWeight > 0
            ? (otherData.totalCostVariance || 0) / costVarWeight
            : 0;
        otherData.avgDeliveryVarianceDays = deliveryWeight > 0 ? weightedAvgDelivery / deliveryWeight : 0;
        otherData.pctPartsInvoiced = invoicedPartsWeight > 0 ? weightedPctInvoiced / invoicedPartsWeight : 0;

        const totalParts = otherData.partsOrdered || 1;
        otherData.avgPartValue = totalParts > 0 ? totalVolume / totalParts : 0;

        let weightedPctOemVol = 0;
        let weightedPctOemCnt = 0;
        let weightedPctLkqVol = 0;
        let weightedPctLkqCnt = 0;
        let weightedPctAmVol = 0;
        let weightedPctAmCnt = 0;
        remaining.forEach(item => {
            const vw = (item.orderVolume || 0) / totalVolume;
            const cw = (item.orderCount || 0) / totalOrders;
            weightedPctOemVol += (item.pctOemVolume || 0) * vw;
            weightedPctOemCnt += (item.pctOemCount || 0) * cw;
            weightedPctLkqVol += (item.pctLkqVolume || 0) * vw;
            weightedPctLkqCnt += (item.pctLkqCount || 0) * cw;
            weightedPctAmVol += (item.pctAftermarketVolume || 0) * vw;
            weightedPctAmCnt += (item.pctAftermarketCount || 0) * cw;
        });
        otherData.pctOemVolume = weightedPctOemVol;
        otherData.pctOemCount = weightedPctOemCnt;
        otherData.pctLkqVolume = weightedPctLkqVol;
        otherData.pctLkqCount = weightedPctLkqCnt;
        otherData.pctAftermarketVolume = weightedPctAmVol;
        otherData.pctAftermarketCount = weightedPctAmCnt;

        // For medians, use simple average as approximation (true median would require all underlying data)
        let sumMedianGM = 0;
        let sumMedianGMWoPM = 0;
        let sumMedianOrderSize = 0;
        remaining.forEach(item => {
            sumMedianGM += item.medianGrossMarginPct || 0;
            sumMedianGMWoPM += item.medianGrossMarginPctWoPM || 0;
            sumMedianOrderSize += item.medianOrderSize || 0;
        });
        otherData.medianGrossMarginPct = remaining.length > 0 ? sumMedianGM / remaining.length : 0;
        otherData.medianGrossMarginPctWoPM = remaining.length > 0 ? sumMedianGMWoPM / remaining.length : 0;
        otherData.medianOrderSize = remaining.length > 0 ? sumMedianOrderSize / remaining.length : 0;

        let sumMedianPartValue = 0;
        remaining.forEach(item => { sumMedianPartValue += item.medianPartValue || 0; });
        otherData.medianPartValue = remaining.length > 0 ? sumMedianPartValue / remaining.length : 0;

        const moDenom = otherData.moEligibleOrderCount || 0;
        otherData.avgMissedOpportunity = moDenom > 0
            ? (otherData.missedOpportunity ?? 0) / moDenom
            : 0;

        return {
            displayedItems: top25,
            otherRow: otherData as DomainStatItem,
            otherItems: remaining
        };
    }, [domain, sortedItems, effectiveVendorGroupBy]);

    const filteredDisplayedItems = useMemo(() => {
        const q = tableSearchQuery.trim().toLowerCase();
        if (!q) return displayedItems;
        return displayedItems.filter(item => {
            if (item.name?.toLowerCase().includes(q)) return true;
            for (const col of tableColumns) {
                const val = getValue(item, col);
                if (val != null) {
                    const cfg = COLUMN_CONFIG[col];
                    const formatted = cfg?.format ? cfg.format(val as number) : String(val);
                    if (formatted?.toLowerCase().includes(q)) return true;
                }
            }
            return false;
        });
    }, [displayedItems, tableSearchQuery, tableColumns, getValue]);

    // CSV Export functionality
    const handleExportCSV = useCallback(() => {
        if (!sortedItems || sortedItems.length === 0) return;

        // Build CSV header
        const exportColumns = tableExperienceKey ? tableColumns : effectiveDisplayColumns;
        const headers = [domainLabel, ...exportColumns.map(col => getColumnLabel(col, domain))];

        const allItems = [...displayedItems, ...otherItems];
        const rows = allItems.map(item => [
            item.name,
            ...exportColumns.map(col => {
                const value = getValue(item, col);
                // Return raw number for CSV (no formatting)
                return value ?? '';
            })
        ]);

        // Add totals row
        const totalsRow = [
            'Total',
            ...exportColumns.map(col => {
                const value = getValue(totals, col);
                return value ?? '';
            })
        ];
        rows.push(totalsRow);

        // Convert to CSV string
        const csvContent = [
            headers.join(','),
            ...rows.map(row => row.map(cell => {
                // Escape quotes and wrap in quotes if contains comma or newline
                const cellStr = String(cell);
                if (cellStr.includes(',') || cellStr.includes('"') || cellStr.includes('\n')) {
                    return `"${cellStr.replace(/"/g, '""')}"`;
                }
                return cellStr;
            }).join(','))
        ].join('\n');

        // Create and trigger download
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', `${domain}_report_${new Date().toISOString().split('T')[0]}.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    }, [sortedItems, displayedItems, otherItems, tableExperienceKey, tableColumns, effectiveDisplayColumns, totals, domain, domainLabel, getValue]);

    // Table-header sorting auto-persists immediately (merged into the saved blob),
    // unlike the rest of the view which only persists on explicit "Save changes".
    const persistTableSort = useCallback((nextBy: SortableColumn | null, nextOrder: 'asc' | 'desc') => {
        if (!viewPrefsKey) return;
        const current = loadViewPrefs<Partial<PersistedDomainView>>(viewPrefsKey, {});
        saveViewPrefs(viewPrefsKey, { ...current, tableSortBy: nextBy, tableSortOrder: nextOrder });
    }, [viewPrefsKey]);

    // Handle table column header click for client-side sorting
    // Cycle: none -> asc -> desc -> none
    const handleTableSort = (col: SortableColumn) => {
        let nextBy: SortableColumn | null;
        let nextOrder: 'asc' | 'desc';
        if (tableSortBy !== col) {
            // Different column: start with ascending
            nextBy = col;
            nextOrder = 'asc';
        } else if (tableSortOrder === 'asc') {
            // Same column, was asc: switch to desc
            nextBy = col;
            nextOrder = 'desc';
        } else {
            // Same column, was desc: reset to no sort
            nextBy = null;
            nextOrder = 'asc';
        }
        setTableSortBy(nextBy);
        setTableSortOrder(nextOrder);
        persistTableSort(nextBy, nextOrder);
    };

    // Get sort direction for icon display
    const getSortDirection = (col: SortableColumn): SortIconDirection => {
        if (tableSortBy !== col) return 'none';
        return tableSortOrder;
    };

    const renderCellContent = (item: DomainStatItem | DomainStatsTotals, col: ColumnKey) => {
        const value = getValue(item, col);
        const config = COLUMN_CONFIG[col];
        const format = config?.format || ((v: number) => (v || 0).toLocaleString());

        // Show dash for null/undefined values (data doesn't exist yet)
        if (value === null || value === undefined) {
            return '—';
        }

        const formattedValue = format(value);

        // If not showing prior, just return the formatted value
        if (!showPrior) {
            return formattedValue;
        }

        // Do not render prior/change for totals row
        if (item === totals) {
            return formattedValue;
        }

        const changeMap = item.change;
        const changeKey = getChangeMapKey(col);
        if (changeKey === null) {
            return formattedValue;
        }

        const change = changeMap?.[changeKey];
        // Do not show prior for agreedDiscount (it is static)
        if (changeKey === 'agreedDiscount') {
            return formattedValue;
        }

        // If no change data available, show NA
        if (change === undefined || change === null || !isFinite(change)) {
            return (
                <Flex align="center" justify="flex-end" gap={2}>
                    <Text>{formattedValue}</Text>
                    <Text as="span" fontStyle="italic" color="gray.400" fontSize="xs">
                        NA
                    </Text>
                </Flex>
            );
        }

        const isPositiveChange = change >= 0;

        // Determine color based on whether metric is "negative" (lower is better)
        let color = "gray.500";
        if (config?.isNegative) {
            // For negative metrics (like missed opportunity), decrease is good
            color = isPositiveChange ? "red.500" : "green.500";
        } else {
            // For positive metrics (like volume), increase is good
            color = isPositiveChange ? "green.500" : "red.500";
        }

        const IconComponent = isPositiveChange ? TbArrowUp : TbArrowDown;
        // Use "bps" for percentage metrics, "%" for dollar/number metrics
        const changeSuffix = config?.type === 'percent' ? 'bps' : '%';
        // No decimals - round to integer
        const changeDisplay = Math.round(Math.abs(change));

        return (
            <Flex align="center" justify="flex-end" gap={2}>
                <Text>{formattedValue}</Text>
                <Flex align="center" gap={0.5} fontStyle="italic">
                    <Icon as={IconComponent as React.ElementType} color={color} boxSize={3} />
                    <Text fontSize="xs" color={color} fontWeight="medium">
                        {changeDisplay}{changeSuffix}
                    </Text>
                </Flex>
            </Flex>
        );
    };

    const renderHeader = (col: ColumnKey) => {
        const headerStyles = {
            fontSize: "xs",
            fontWeight: "semibold",
            textTransform: "uppercase" as const,
            letterSpacing: "0.05em",
            color: tableSortBy === col ? '#005BC4' : 'gray.600',
            _hover: { color: '#005BC4' },
            cursor: "pointer",
            transition: "color 0.2s",
            whiteSpace: "normal" as const,
            textAlign: "right" as const,
            lineHeight: 1.3
        };

        const SortIcon = () => (
            <Box lineHeight={0} color={tableSortBy === col ? '#005BC4' : 'gray.400'} flexShrink={0}>
                <SortArrowIcon direction={getSortDirection(col)} size={12} />
            </Box>
        );

        return (
            <Flex
                as="button"
                type="button"
                align="flex-end"
                justify="flex-end"
                gap="6px"
                w="100%"
                h="100%"
                {...headerStyles}
                onClick={() => handleTableSort(col)}
                pb="1px"
            >
                <Text as="span">{getColumnLabel(col, domain)}</Text>
                <SortIcon />
            </Flex>
        );
    };

    const renderTable = (enableVerticalScroll = false) => {
        if (!items || items.length === 0) {
            return (
                <Box h="300px" display="flex" alignItems="center" justifyContent="center">
                    <Text color="gray.500">No data available for this period</Text>
                </Box>
            );
        }

        return (
            <Box
                h={enableVerticalScroll ? '100%' : 'auto'}
                minH={enableVerticalScroll ? 0 : undefined}
                maxH="none"
                overflow={enableVerticalScroll ? 'hidden' : 'visible'}
                sx={{
                    ...tableSettingsSx,
                    '& table': {
                        width: 'max-content !important',
                        minWidth: '100%',
                    },
                    '& thead th': {
                        position: 'sticky',
                        top: 0,
                        zIndex: 4,
                        bg: 'gray.50',
                        borderBottom: '1px solid',
                        borderColor: 'gray.200',
                    },
                    '& th:first-of-type': {
                        position: 'sticky',
                        left: 0,
                        zIndex: 6,
                        bg: 'gray.50',
                    },
                    '& td:first-of-type': {
                        position: 'sticky',
                        left: 0,
                        zIndex: 2,
                        bg: 'white',
                    },
                    '& tr[data-total-row] > td:first-of-type': {
                        bg: 'blue.50',
                    },
                    '& tr[data-marketplace-row] > td:first-of-type': {
                        bg: 'gray.50',
                    },
                    '& tr:hover > td:first-of-type': {
                        bg: 'gray.50',
                    },
                    '& tr[data-total-row]:hover > td:first-of-type': {
                        bg: 'blue.50',
                    },
                    '& tr[data-marketplace-row]:hover > td:first-of-type': {
                        bg: 'gray.50',
                    },
                }}
            >
                <Box overflowX="auto" overflowY={enableVerticalScroll ? 'auto' : 'hidden'} h={enableVerticalScroll ? '100%' : 'auto'}>
                    <Table variant="simple" size="sm">
                        <Thead bg="gray.50">
                            <Tr>
                                <ResizableTh
                                    columnKey="__entity__"
                                    storedWidth={colWidths.__entity__}
                                    onResize={updateWidth}
                                    minWidth={100}
                                    py={3}
                                    px={2}
                                >
                                    <Text
                                        fontSize="xs"
                                        fontWeight="semibold"
                                        textTransform="uppercase"
                                        letterSpacing="0.05em"
                                        color="gray.600"
                                    >
                                        {domainLabel}
                                    </Text>
                                </ResizableTh>
                                {tableColumns.map(col => (
                                    <ResizableTh
                                        key={col}
                                        columnKey={col}
                                        storedWidth={colWidths[col]}
                                        onResize={updateWidth}
                                        minWidth={72}
                                        py={3}
                                        px={2}
                                        isNumeric
                                    >
                                        {renderHeader(col)}
                                    </ResizableTh>
                                ))}
                            </Tr>
                        </Thead>
                        <Tbody>
                            {filteredDisplayedItems.map((item, index) => {
                                const rowKey = `${item.id}-${item.name}-${index}`;
                                const rowSources = marketplaceTableActive ? getRowSources(item) : [];
                                const isRowExpandable = rowSources.length > 0;
                                const isRowExpanded = isRowExpandable && expandedRows.has(rowKey);
                                return (
                                    <Fragment key={rowKey}>
                                        <Tr _hover={{ bg: 'gray.50' }}>
                                            <Td fontWeight="medium" color="gray.800" px={2}>
                                                {isRowExpandable ? (
                                                    <HStack spacing={1} cursor="pointer" onClick={() => toggleExpandedRow(rowKey)}>
                                                        <Icon
                                                            as={(isRowExpanded ? TbChevronDown : TbChevronRight) as React.ElementType}
                                                            boxSize={4}
                                                            color="gray.400"
                                                        />
                                                        <Text as="span">{item.name}</Text>
                                                    </HStack>
                                                ) : item.name}
                                            </Td>
                                            {tableColumns.map(col => (
                                                <Td key={col} px={2} isNumeric>
                                                    {renderCellContent(item, col)}
                                                </Td>
                                            ))}
                                        </Tr>
                                        {isRowExpanded && rowSources.map((src, sIdx) => {
                                            const sourceItem = item.bySource![src] as DomainStatItem;
                                            return (
                                                <Tr key={`${rowKey}-${src}`} bg="gray.50" data-marketplace-row>
                                                    <Td px={2} pl={8} color="gray.700">
                                                        <HStack spacing={2}>
                                                            <Box w="8px" h="8px" borderRadius="full" bg={getSourceColor(src, sIdx)} flexShrink={0} />
                                                            <Text as="span" fontSize="sm">{getSourceLabel(src)}</Text>
                                                        </HStack>
                                                    </Td>
                                                    {tableColumns.map(col => (
                                                        <Td key={col} px={2} isNumeric>
                                                            {renderCellContent(sourceItem, col)}
                                                        </Td>
                                                    ))}
                                                </Tr>
                                            );
                                        })}
                                    </Fragment>
                                );
                            })}
                            {/* Other row (for domains with more than 25 items) */}
                            {otherRow && !tableSearchQuery.trim() && (
                                <Tr
                                    _hover={{ bg: 'gray.50' }}
                                    cursor="pointer"
                                    onClick={() => setIsOtherExpanded(prev => !prev)}
                                >
                                    <Td fontWeight="medium" color="gray.800" px={2}>
                                        <HStack spacing={1}>
                                            <Icon
                                                as={TbChevronDown as React.ElementType}
                                                boxSize={4}
                                                transform={isOtherExpanded ? 'rotate(180deg)' : 'rotate(0deg)'}
                                                transition="transform 0.2s"
                                            />
                                            <Text as="span">Other ({otherItems.length})</Text>
                                        </HStack>
                                    </Td>
                                    {tableColumns.map(col => (
                                        <Td key={col} px={2} isNumeric>
                                            {renderCellContent(otherRow, col)}
                                        </Td>
                                    ))}
                                </Tr>
                            )}
                            {otherRow && !tableSearchQuery.trim() && isOtherExpanded && otherItems.map((item, index) => (
                                <Tr key={`other-expanded-${item.id}-${item.name}-${index}`} _hover={{ bg: 'gray.50' }}>
                                    <Td color="gray.700" px={2} pl={7}>{item.name}</Td>
                                    {tableColumns.map(col => (
                                        <Td key={col} px={2} isNumeric>
                                            {renderCellContent(item, col)}
                                        </Td>
                                    ))}
                                </Tr>
                            ))}
                            {/* Totals row */}
                            <Tr bg="blue.50" fontWeight="bold" data-total-row>
                                <Td color="gray.800" px={2}>Total</Td>
                                {tableColumns.map(col => (
                                    <Td key={col} px={2} isNumeric>
                                        {renderCellContent(totals, col)}
                                    </Td>
                                ))}
                            </Tr>
                        </Tbody>
                    </Table>
                </Box>
            </Box>
        );
    };

    const hasItems = items.length > 0;
    const isInitialLoading = (!!isFetching || isLoading) && !hasItems;
    const isRefetching = !!isFetching && hasItems;

    const showHeaderSpinner = isRefetching;

    const settingsDrawerContent = (
        <AppDrawer
            isOpen={settingsDrawerRaw.isOpen}
            onClose={handleDrawerDiscard}
            title={viewType === 'graph' ? 'Chart Settings' : 'Table Settings'}
            size="md"
            bodyProps={{ pt: 4 }}
            footer={
                <>
                    <Button size="action" variant="outlineGray" onClick={handleDrawerDiscard}>
                        Cancel
                    </Button>
                    <Button size="action" bg="#0071E3" color="white" _hover={{ bg: '#3544a0' }} onClick={handleDrawerSave}>
                        Save
                    </Button>
                </>
            }
        >
            <VStack spacing={6} align="stretch">
                        {/* Auto-generated preview */}
                        <Box bg="gray.50" borderRadius="md" p={3} border="1px solid" borderColor="gray.300">
                            <Text fontSize="sm" fontWeight="semibold" color="gray.700">
                                {viewType === 'graph' ? `${domainLabel} ${metricLabel}`.trim() : domainLabel}
                            </Text>
                            {currentMetricOption?.tooltip && (
                                <Text fontSize="xs" color="gray.500" mt={1}>{currentMetricOption.tooltip}</Text>
                            )}
                        </Box>

                        <Accordion allowMultiple defaultIndex={[0, 1, 2]}>
                            {/* View Type Section */}
                            <Box border="1px solid" borderColor="gray.200" borderRadius="lg" overflow="visible" mb={3}>
                                <AccordionItem border="none">
                                    <AccordionButton px={4} py={3} _hover={{ bg: 'transparent' }}>
                                        <Text flex={1} textAlign="left" fontWeight="semibold" fontSize="md" color="gray.800">View Type</Text>
                                        <AccordionIcon />
                                    </AccordionButton>
                                    <AccordionPanel px={4} pb={4} pt={1}>
                                        <SimpleGrid columns={2} spacing={3}>
                                            {([
                                                { value: 'graph' as ViewType, label: 'Bar Chart', icon: TbChartBar },
                                                { value: 'table' as ViewType, label: 'Table', icon: TbTable },
                                            ]).map(vt => (
                                                <Flex
                                                    key={vt.value}
                                                    align="center"
                                                    gap={3}
                                                    px={4}
                                                    py={3}
                                                    borderRadius="lg"
                                                    border="2px solid"
                                                    borderColor={viewType === vt.value ? '#0071E3' : 'gray.200'}
                                                    bg={viewType === vt.value ? '#E6F1FD' : 'white'}
                                                    cursor="pointer"
                                                    _hover={{ borderColor: '#0071E3' }}
                                                    transition="all 0.15s"
                                                    onClick={() => setViewType(vt.value)}
                                                >
                                                    <Icon as={vt.icon as React.ElementType} boxSize={5} color={viewType === vt.value ? '#0071E3' : 'gray.500'} />
                                                    <Text fontSize="sm" fontWeight="medium" color={viewType === vt.value ? '#0071E3' : 'gray.700'}>{vt.label}</Text>
                                                </Flex>
                                            ))}
                                        </SimpleGrid>
                                    </AccordionPanel>
                                </AccordionItem>
                            </Box>

                            {/* Group by Section - vendor only */}
                            {showVendorGroupByControl && (
                                <Box border="1px solid" borderColor="gray.200" borderRadius="lg" overflow="visible" position="relative" zIndex={1.75} mb={3}>
                                    <AccordionItem border="none">
                                        <AccordionButton px={4} py={3} _hover={{ bg: 'transparent' }}>
                                            <Text flex={1} textAlign="left" fontWeight="semibold" fontSize="md" color="gray.800">Group by</Text>
                                            <AccordionIcon />
                                        </AccordionButton>
                                        <AccordionPanel px={4} pb={4} pt={1}>
                                            {renderVendorGroupByControl()}
                                        </AccordionPanel>
                                    </AccordionItem>
                                </Box>
                            )}

                            {/* Metric Section */}
                            {viewType === 'graph' && (
                                <Box border="1px solid" borderColor="gray.200" borderRadius="lg" overflow="visible" position="relative" zIndex={2} mb={3}>
                                    <AccordionItem border="none">
                                        <AccordionButton px={4} py={3} _hover={{ bg: 'transparent' }}>
                                            <Text flex={1} textAlign="left" fontWeight="semibold" fontSize="md" color="gray.800">Metric</Text>
                                            <AccordionIcon />
                                        </AccordionButton>
                                        <AccordionPanel px={4} pb={4} pt={1}>
                                            <VStack spacing={4} align="stretch">
                                                <FormControl>
                                                    <FormLabel fontSize="sm" fontWeight="medium" color="gray.600">Metric</FormLabel>
                                                    <MetricPopoverSelect
                                                        value={chartMetric}
                                                        options={metricOptions}
                                                        onChange={handleCondensedMetricChange}
                                                        width="100%"
                                                        domain={domain}
                                                    />
                                                </FormControl>
                                                {activeVariantGroup && (
                                                    <FormControl>
                                                        <FormLabel fontSize="sm" fontWeight="medium" color="gray.600">
                                                            {activeVariantGroup.variants[0].label === 'Volume' ? 'Measure by' : 'Aggregation'}
                                                        </FormLabel>
                                                        <HStack spacing={0}>
                                                            {activeVariantGroup.variants.map((v, i) => {
                                                                const isActive = v.value === chartMetric;
                                                                const nextIsActive = i < activeVariantGroup.variants.length - 1 && activeVariantGroup.variants[i + 1].value === chartMetric;
                                                                return (
                                                                    <Button
                                                                        key={v.value}
                                                                        size="sm"
                                                                        h="32px"
                                                                        flex={1}
                                                                        borderRadius={0}
                                                                        borderLeftRadius={i === 0 ? 'md' : 0}
                                                                        borderRightRadius={i === activeVariantGroup.variants.length - 1 ? 'md' : 0}
                                                                        border="1px solid"
                                                                        borderColor={isActive ? '#0071E3' : 'gray.200'}
                                                                        borderLeftColor={i > 0 ? (isActive ? '#0071E3' : 'transparent') : undefined}
                                                                        borderRightColor={nextIsActive ? 'transparent' : undefined}
                                                                        ml={i > 0 ? '-1px' : undefined}
                                                                        zIndex={isActive ? 1 : 0}
                                                                        bg={isActive ? '#E6F1FD' : 'white'}
                                                                        color={isActive ? '#0071E3' : 'gray.600'}
                                                                        fontWeight={isActive ? 'semibold' : 'normal'}
                                                                        fontSize="sm"
                                                                        _hover={{ bg: isActive ? '#E6F1FD' : 'gray.50' }}
                                                                        onClick={() => handleMetricChange(v.value as ChartMetricType)}
                                                                    >
                                                                        {v.label}
                                                                    </Button>
                                                                );
                                                            })}
                                                        </HStack>
                                                    </FormControl>
                                                )}
                                            </VStack>
                                        </AccordionPanel>
                                    </AccordionItem>
                                </Box>
                            )}

                            {/* Configuration Section */}
                            <Box border="1px solid" borderColor="gray.200" borderRadius="lg" overflow="visible" position="relative" zIndex={1} mb={viewType === 'table' ? 3 : 0}>
                                <AccordionItem border="none">
                                    <AccordionButton px={4} py={3} _hover={{ bg: 'transparent' }}>
                                        <Text flex={1} textAlign="left" fontWeight="semibold" fontSize="md" color="gray.800">Configuration</Text>
                                        <AccordionIcon />
                                    </AccordionButton>
                                    <AccordionPanel px={4} pb={4} pt={1}>
                                        <VStack spacing={4} align="stretch">
                                            {comparisonSupported && (
                                            <Flex align="center" justify="space-between" cursor="pointer" onClick={() => setShowComparison(v => !v)}>
                                                <HStack spacing={3}>
                                                    <Icon as={TbEye as React.ElementType} boxSize={5} color="gray.500" />
                                                    <Box>
                                                        <Text fontSize="sm" fontWeight="semibold" color="gray.800">
                                                            Show prior period
                                                        </Text>
                                                        <Text fontSize="xs" color="gray.500">
                                                            Display change indicators vs. previous period
                                                        </Text>
                                                    </Box>
                                                </HStack>
                                                <Switch isChecked={showComparison} pointerEvents="none" colorScheme="blue" size="md" />
                                            </Flex>
                                            )}
                                            <ExcludeEvsToggle />
                                            {canShowMarketplaceToggle && (
                                                <Flex
                                                    align="center"
                                                    justify="space-between"
                                                    cursor={marketplaceDisabledForChart ? 'not-allowed' : 'pointer'}
                                                    opacity={marketplaceDisabledForChart ? 0.5 : 1}
                                                    onClick={() => { if (!marketplaceDisabledForChart) setMarketplaceView(v => !v); }}
                                                >
                                                    <HStack spacing={3}>
                                                        <Icon as={TbBuildingStore as React.ElementType} boxSize={5} color="gray.500" />
                                                        <Box>
                                                            <Text fontSize="sm" fontWeight="semibold" color="gray.800">
                                                                Show marketplace breakdown
                                                            </Text>
                                                            <Text fontSize="xs" color="gray.500">
                                                                {viewType === 'table'
                                                                    ? 'Expand each row into per-marketplace rows'
                                                                    : marketplaceDisabledForChart
                                                                        ? 'This metric can\u2019t be split by marketplace'
                                                                        : 'Split bars by marketplace (clustered)'}
                                                            </Text>
                                                        </Box>
                                                    </HStack>
                                                    <Switch isChecked={marketplaceView && !marketplaceDisabledForChart} pointerEvents="none" colorScheme="blue" size="md" />
                                                </Flex>
                                            )}
                                            {viewType === 'graph' && (
                                                <FormControl>
                                                    <FormLabel fontSize="sm" fontWeight="medium" color="gray.600">Default sort order</FormLabel>
                                                    <HStack spacing={0}>
                                                        {(['desc', 'asc'] as const).map((dir, i) => {
                                                            const isActive = chartSortOrder === dir;
                                                            return (
                                                                <Button key={dir} size="sm" h="32px" flex={1} borderRadius={0} borderLeftRadius={i === 0 ? 'md' : 0} borderRightRadius={i === 1 ? 'md' : 0} border="1px solid" borderColor={isActive ? '#0071E3' : 'gray.200'} borderLeftColor={i > 0 ? (isActive ? '#0071E3' : 'transparent') : undefined} ml={i > 0 ? '-1px' : undefined} zIndex={isActive ? 1 : 0} bg={isActive ? '#E6F1FD' : 'white'} color={isActive ? '#0071E3' : 'gray.600'} fontWeight={isActive ? 'semibold' : 'normal'} fontSize="sm" _hover={{ bg: isActive ? '#E6F1FD' : 'gray.50' }} onClick={() => setChartSortOrder(dir)}>
                                                                    {dir === 'desc' ? 'Descending' : 'Ascending'}
                                                                </Button>
                                                            );
                                                        })}
                                                    </HStack>
                                                </FormControl>
                                            )}
                                        </VStack>
                                    </AccordionPanel>
                                </AccordionItem>
                            </Box>

                            {/* Columns Section - table view only */}
                            {viewType === 'table' && draftColumnRows.length > 0 && (
                                <Box border="1px solid" borderColor="gray.200" borderRadius="lg" overflow="visible" mb={3}>
                                    <AccordionItem border="none">
                                        <AccordionButton px={4} py={3} _hover={{ bg: 'transparent' }}>
                                            <Text flex={1} textAlign="left" fontWeight="semibold" fontSize="md" color="gray.800">Columns</Text>
                                            <AccordionIcon />
                                        </AccordionButton>
                                        <AccordionPanel px={4} pb={4} pt={1}>
                                            <Box mb={3} px={3} py={2.5} bg="gray.50" border="1px solid" borderColor="gray.200" borderRadius="md">
                                                <Text fontSize="xs" color="gray.500">Drag to reorder. Expand a field to turn on Avg, Median, or Total as separate columns.</Text>
                                            </Box>
                                            <VStack spacing={0} align="stretch">
                                                {draftColumnRows
                                                    .filter((row) => !(row.key === 'agreedDiscount' && effectiveVendorGroupBy === 'preference'))
                                                    .filter((row) => !(
                                                        effectiveVendorGroupBy === 'preference'
                                                        && (row.key === 'quoteRate' || row.key === 'quoteOpportunities' || row.key === 'quotesSubmitted')
                                                    ))
                                                    .map((row) => {
                                                        const hasChildren = Boolean(row.children?.length);
                                                        const isExpanded = hasChildren && expandedColumnGroups.has(row.key);
                                                        return (
                                                            <Box key={row.key}>
                                                                <Flex
                                                                    align="center"
                                                                    gap={2}
                                                                    py={2}
                                                                    px={2}
                                                                    borderRadius="md"
                                                                    _hover={{ bg: 'gray.50' }}
                                                                    draggable={!row.pinned}
                                                                    onDragStart={(e) => {
                                                                        if (row.pinned) return;
                                                                        setColDragKey(row.key);
                                                                        e.dataTransfer.effectAllowed = 'move';
                                                                        e.dataTransfer.setData('text/plain', row.key);
                                                                    }}
                                                                    onDragEnd={() => setColDragKey(null)}
                                                                    onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; }}
                                                                    onDrop={(e) => {
                                                                        e.preventDefault();
                                                                        const from = e.dataTransfer.getData('text/plain') || colDragKey;
                                                                        if (from && from !== row.key) handleDraftColumnReorder(from, row.key);
                                                                        setColDragKey(null);
                                                                    }}
                                                                    opacity={colDragKey === row.key ? 0.6 : 1}
                                                                >
                                                                    <Checkbox
                                                                        isChecked={row.visible}
                                                                        isDisabled={row.pinned}
                                                                        onChange={() => handleDraftColumnToggle(row.key)}
                                                                        flexShrink={0}
                                                                    />
                                                                    <Text fontSize="sm" flex={1} noOfLines={1}>{row.label}</Text>
                                                                    {hasChildren && (
                                                                        <Icon
                                                                            as={TbChevronDown as React.ElementType}
                                                                            boxSize={4}
                                                                            color="gray.400"
                                                                            cursor="pointer"
                                                                            transform={isExpanded ? 'rotate(0deg)' : 'rotate(-90deg)'}
                                                                            transition="transform 0.15s"
                                                                            flexShrink={0}
                                                                            onClick={(e) => {
                                                                                e.stopPropagation();
                                                                                setExpandedColumnGroups((prev) => {
                                                                                    const next = new Set(prev);
                                                                                    if (next.has(row.key)) next.delete(row.key);
                                                                                    else next.add(row.key);
                                                                                    return next;
                                                                                });
                                                                            }}
                                                                        />
                                                                    )}
                                                                    {!row.pinned && <Icon as={TbGripVertical as React.ElementType} boxSize={4} color="gray.400" cursor="grab" flexShrink={0} />}
                                                                </Flex>
                                                                {isExpanded && row.children?.map((child) => (
                                                                    <Flex
                                                                        key={child.key}
                                                                        align="center"
                                                                        gap={2}
                                                                        py={1.5}
                                                                        pl={8}
                                                                        pr={2}
                                                                        borderRadius="md"
                                                                        _hover={{ bg: 'gray.50' }}
                                                                        draggable
                                                                        onDragStart={(e) => {
                                                                            setColDragKey(child.key);
                                                                            e.dataTransfer.effectAllowed = 'move';
                                                                            e.dataTransfer.setData('text/plain', child.key);
                                                                        }}
                                                                        onDragEnd={() => setColDragKey(null)}
                                                                        onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; }}
                                                                        onDrop={(e) => {
                                                                            e.preventDefault();
                                                                            const from = e.dataTransfer.getData('text/plain') || colDragKey;
                                                                            if (from && from !== child.key) handleDraftColumnReorder(from, child.key);
                                                                            setColDragKey(null);
                                                                        }}
                                                                        opacity={colDragKey === child.key ? 0.6 : 1}
                                                                    >
                                                                        <Checkbox
                                                                            isChecked={child.visible}
                                                                            onChange={() => handleDraftColumnToggle(child.key)}
                                                                            flexShrink={0}
                                                                        />
                                                                        <Text fontSize="sm" flex={1} noOfLines={1}>{child.label}</Text>
                                                                        <Icon as={TbGripVertical as React.ElementType} boxSize={4} color="gray.400" cursor="grab" flexShrink={0} />
                                                                    </Flex>
                                                                ))}
                                                            </Box>
                                                        );
                                                    })}
                                            </VStack>
                                            <>
                                                <Divider my={4} />
                                                <Flex align="center" cursor="pointer" onClick={handleResetColumnsToDefault} _hover={{ bg: 'gray.50' }} borderRadius="md" p={2} mx={-2} bg={pendingResetColumns ? 'blue.50' : undefined}>
                                                    <HStack spacing={3}>
                                                        <Icon as={TbArrowBackUp as React.ElementType} boxSize={5} color="gray.500" />
                                                        <Box>
                                                            <Text fontSize="sm" fontWeight="semibold" color="gray.800">Reset Columns to Default</Text>
                                                            <Text fontSize="xs" color="gray.500">Restore column visibility and order</Text>
                                                        </Box>
                                                    </HStack>
                                                </Flex>
                                            </>
                                        </AccordionPanel>
                                    </AccordionItem>
                                </Box>
                            )}

                            {/* Display Section - table view only */}
                            {viewType === 'table' && (
                                <Box border="1px solid" borderColor="gray.200" borderRadius="lg" overflow="visible">
                                    <AccordionItem border="none">
                                        <AccordionButton px={4} py={3} _hover={{ bg: 'transparent' }}>
                                            <Text flex={1} textAlign="left" fontWeight="semibold" fontSize="md" color="gray.800">Display</Text>
                                            <AccordionIcon />
                                        </AccordionButton>
                                        <AccordionPanel px={4} pb={4} pt={1}>
                                            <Box mb={4}>
                                                <Text fontSize="sm" fontWeight="bold" color="gray.900" mb={3}>Table Density</Text>
                                                <VStack spacing={3} align="stretch">
                                                    {([
                                                        { key: 'compact' as const, label: 'Compact', icon: TbLayoutList },
                                                        { key: 'detailed' as const, label: 'Detailed', icon: TbListDetails },
                                                    ]).map(option => {
                                                        const isSelected = draftTableUi.density === option.key;
                                                        return (
                                                            <Box
                                                                key={option.key}
                                                                as="button"
                                                                type="button"
                                                                onClick={() => setDraftTableUi((prev) => ({ ...prev, density: option.key }))}
                                                                border="1px solid"
                                                                borderColor={isSelected ? '#0071E3' : 'gray.200'}
                                                                borderRadius="lg"
                                                                px={4}
                                                                py={3}
                                                                cursor="pointer"
                                                                bg={isSelected ? '#E6F1FD' : 'white'}
                                                                _hover={{ borderColor: isSelected ? '#0071E3' : 'gray.300', bg: isSelected ? '#E6F1FD' : 'gray.50' }}
                                                                transition="all 0.15s"
                                                                textAlign="left"
                                                                w="100%"
                                                            >
                                                                <HStack spacing={3}>
                                                                    <Flex align="center" justify="center" w="20px" h="20px" borderRadius="full" border="2px solid" borderColor={isSelected ? '#0071E3' : 'gray.300'} flexShrink={0}>
                                                                        {isSelected && <Box w="10px" h="10px" borderRadius="full" bg="#0071E3" />}
                                                                    </Flex>
                                                                    <Icon as={option.icon as React.ElementType} boxSize={5} color={isSelected ? '#0071E3' : 'gray.500'} />
                                                                    <Text fontSize="sm" fontWeight="medium" color={isSelected ? '#0071E3' : 'gray.700'}>{option.label}</Text>
                                                                </HStack>
                                                            </Box>
                                                        );
                                                    })}
                                                </VStack>
                                            </Box>
                                            <Divider mb={4} />
                                            <Flex align="center" justify="space-between" cursor="pointer" onClick={() => setDraftTableUi((prev) => ({ ...prev, columnDividers: !prev.columnDividers }))}>
                                                <HStack spacing={3}>
                                                    <Icon as={TbColumns as React.ElementType} boxSize={5} color="gray.500" />
                                                    <Box>
                                                        <Text fontSize="sm" fontWeight="semibold" color="gray.800">Column Dividers</Text>
                                                        <Text fontSize="xs" color="gray.500">Display vertical dividers between each column</Text>
                                                    </Box>
                                                </HStack>
                                                <Switch isChecked={draftTableUi.columnDividers} pointerEvents="none" colorScheme="blue" size="md" />
                                            </Flex>
                                            {(hasCustomWidths || pendingResetWidths) && (
                                                <>
                                                    <Divider my={4} />
                                                    <Flex align="center" justify="space-between" cursor="pointer" onClick={() => setPendingResetWidths(true)} _hover={{ bg: 'gray.50' }} borderRadius="md" p={2} mx={-2} bg={pendingResetWidths ? 'blue.50' : undefined}>
                                                        <HStack spacing={3}>
                                                            <Icon as={TbArrowBackUp as React.ElementType} boxSize={5} color="gray.500" />
                                                            <Box>
                                                                <Text fontSize="sm" fontWeight="semibold" color="gray.800">Reset Column Widths</Text>
                                                                <Text fontSize="xs" color="gray.500">Restore all columns to their default widths</Text>
                                                            </Box>
                                                        </HStack>
                                                    </Flex>
                                                </>
                                            )}
                                        </AccordionPanel>
                                    </AccordionItem>
                                </Box>
                            )}
                        </Accordion>
            </VStack>
        </AppDrawer>
    );

    const settingsButton = (
        <IconButton
            aria-label="Chart settings"
            icon={<Icon as={TbSettings as React.ElementType} boxSize={4} />}
            size="sm"
            h="32px"
            w="32px"
            variant="outline"
            borderColor="gray.300"
            color="gray.600"
            borderRadius="md"
            _hover={{ bg: 'gray.50' }}
            onClick={openSettingsDrawer}
        />
    );

    const tableSearchBar = (
        <InputGroup size="sm" w="300px">
            <InputLeftElement pointerEvents="none">
                <Icon as={TbSearch as React.ElementType} color="gray.400" boxSize={3.5} />
            </InputLeftElement>
            <Input
                placeholder="Search everything in table…"
                value={tableSearchQuery}
                onChange={(e) => setTableSearchQuery(e.target.value)}
                borderColor="gray.300"
                borderRadius="md"
                fontSize="sm"
                _placeholder={{ color: 'gray.400' }}
                _hover={{ borderColor: 'gray.400' }}
                _focus={{ borderColor: '#0071E3', boxShadow: '0 0 0 1px #0071E3' }}
            />
        </InputGroup>
    );

    const fullscreenToolbar = (
        <Flex justify="space-between" align="center" w="100%">
            <Flex align="center" gap={1.5} minW={0} flexShrink={1}>
                <Text fontSize="sm" fontWeight="semibold" color="gray.600" noOfLines={1}>
                    {viewType === 'graph' ? `${domainLabel} ${metricLabel}`.trim() : domainLabel}
                </Text>
                {showHeaderSpinner && <Spinner size="xs" color="#0071E3" flexShrink={0} />}
                {viewType === 'graph' && currentMetricOption?.tooltip && (
                    <Tooltip label={currentMetricOption.tooltip} placement="top" hasArrow>
                        <Box as="span" display="inline-flex" color="gray.400" _hover={{ color: 'gray.600' }} cursor="help">
                            <Icon as={TbInfoCircle as React.ElementType} boxSize={3.5} />
                        </Box>
                    </Tooltip>
                )}
            </Flex>
            <Flex align="center" gap={2} flexShrink={0}>
                {viewType === 'table' && tableSearchBar}
                {settingsButton}
                <Tooltip label="Export to CSV" hasArrow placement="top">
                    <IconButton aria-label="Export to CSV" icon={<Icon as={TbDownload as React.ElementType} boxSize={4} />} size="sm" h="32px" w="32px" variant="outline" borderColor="gray.300" color="gray.600" borderRadius="md" _hover={{ bg: 'gray.50' }} onClick={handleExportCSV} />
                </Tooltip>
                <Tooltip label={isFullScreen ? 'Exit full screen' : 'Full screen'} placement="top" hasArrow>
                    <IconButton aria-label={isFullScreen ? 'Exit full screen' : 'Full screen'} icon={<Icon as={(isFullScreen ? TbMinimize : TbMaximize) as React.ElementType} boxSize={4} />} size="sm" h="32px" w="32px" variant="outline" borderColor="gray.300" color="gray.600" borderRadius="md" _hover={{ bg: 'gray.50' }} _active={{ bg: 'gray.100' }} onClick={() => setIsFullScreen(v => !v)} />
                </Tooltip>
            </Flex>
        </Flex>
    );

    const cardHeader = (
        <Flex justify="space-between" align="center" px={4} py={2.5} gap={2} flexShrink={0} bg="gray.50" borderTopRadius="lg">
            <Flex align="center" gap={1.5} minW={0} flexShrink={1}>
                <Text fontSize="sm" fontWeight="semibold" color="gray.600" noOfLines={1}>
                    {viewType === 'graph' ? `${domainLabel} ${metricLabel}`.trim() : domainLabel}
                </Text>
                {showHeaderSpinner && <Spinner size="xs" color="#0071E3" flexShrink={0} />}
                {viewType === 'graph' && currentMetricOption?.tooltip && (
                    <Tooltip label={currentMetricOption.tooltip} placement="top" hasArrow>
                        <Box as="span" display="inline-flex" color="gray.400" _hover={{ color: 'gray.600' }} cursor="help">
                            <Icon as={TbInfoCircle as React.ElementType} boxSize={3.5} />
                        </Box>
                    </Tooltip>
                )}
            </Flex>
            <Flex align="center" gap={2} flexShrink={0}>
                {viewType === 'table' && tableSearchBar}
                {settingsButton}
                {viewType === 'graph' && (
                    <Tooltip label={fixedView ? `Fixed View: ON` : `Fixed View: OFF`} placement="top" hasArrow>
                        <IconButton
                            aria-label={fixedView ? 'Unlock view' : 'Lock view'}
                            icon={<Icon as={(fixedView ? TbLock : TbLockOpen) as React.ElementType} boxSize={4} />}
                            size="sm" h="32px" w="32px"
                            variant={fixedView ? 'solid' : 'outline'}
                            bg={fixedView ? '#005BC4' : 'gray.50'}
                            color={fixedView ? 'white' : 'gray.600'}
                            borderColor={fixedView ? '#005BC4' : 'gray.300'}
                            borderRadius="md"
                            _hover={{ bg: fixedView ? '#252f7a' : 'gray.50', borderColor: fixedView ? '#252f7a' : 'gray.400' }}
                            onClick={() => setFixedView(prev => !prev)}
                        />
                    </Tooltip>
                )}
                <Tooltip label="Export to CSV" hasArrow placement="top">
                    <IconButton aria-label="Export to CSV" icon={<Icon as={TbDownload as React.ElementType} boxSize={4} />} size="sm" h="32px" w="32px" variant="outline" borderColor="gray.300" color="gray.600" borderRadius="md" _hover={{ bg: 'gray.50' }} onClick={handleExportCSV} />
                </Tooltip>
                <Tooltip label={isFullScreen ? 'Exit full screen' : 'Full screen'} placement="top" hasArrow>
                    <IconButton aria-label={isFullScreen ? 'Exit full screen' : 'Full screen'} icon={<Icon as={(isFullScreen ? TbMinimize : TbMaximize) as React.ElementType} boxSize={4} />} size="sm" h="32px" w="32px" variant="outline" borderColor="gray.300" color="gray.600" borderRadius="md" _hover={{ bg: 'gray.50' }} _active={{ bg: 'gray.100' }} onClick={() => setIsFullScreen(v => !v)} />
                </Tooltip>
            </Flex>
        </Flex>
    );

    const cardShell = viewType === 'table' ? (
        <Box
            bg="white"
            borderRadius="lg"
            border="1px solid"
            borderColor="gray.200"
            shadow="none"
            w="100%"
            h="auto"
            maxH="none"
            overflow="visible"
        >
            {settingsDrawerContent}
            {cardHeader}
            <Box opacity={isRefetching ? 0.55 : 1} transition="opacity 0.15s">
                {isInitialLoading ? (
                    <Box p={3}>
                        <Skeleton height="400px" borderRadius="md" />
                    </Box>
                ) : (
                    renderTable()
                )}
            </Box>
        </Box>
    ) : (
        <Box
            bg="white"
            borderRadius="lg"
            border="1px solid"
            borderColor="gray.200"
            shadow="none"
            display="flex"
            flexDirection="column"
            w="100%"
            overflow="hidden"
        >
            {settingsDrawerContent}
            {cardHeader}
            <Box flex={1} minH={0} p={3} opacity={isRefetching ? 0.55 : 1} transition="opacity 0.15s">
                {isInitialLoading ? (
                    <Skeleton height={`${height}px`} borderRadius="md" />
                ) : (
                    <VerticalRankingChart data={sortedChartItems} isLoading={false} metric={chartMetric} domainLabel={domainLabel} maxItems={maxChartItems} height={height} comparison={effectiveComparison} preset={preset} showPriorBars={effectiveShowPrior} isVendor={isVendor} isLocation={isLocation} isInsurance={isInsurance} isOEM={isOEM} marketplaceBreakdown={marketplaceChartActive} />
                )}
            </Box>
        </Box>
    );

    return (
        <ReportingFullScreenShell isOpen={isFullScreen} onClose={() => setIsFullScreen(false)} fullscreenToolbar={isFullScreen ? fullscreenToolbar : undefined}>
            {isFullScreen ? (
                <Box flex={1} minH={0} display="flex" flexDirection="column" w="100%" h="100%">
                    {settingsDrawerContent}
                    {viewType === 'graph' ? (
                        <Box flex={1} minH={0} h="100%" display="flex" flexDirection="column" overflow="visible" p={3} opacity={isRefetching ? 0.55 : 1} transition="opacity 0.15s">
                            <VerticalRankingChart data={sortedChartItems} isLoading={false} metric={chartMetric} domainLabel={domainLabel} maxItems={maxChartItems} height="100%" comparison={effectiveComparison} preset={preset} showPriorBars={effectiveShowPrior} isVendor={isVendor} isLocation={isLocation} isInsurance={isInsurance} isOEM={isOEM} marketplaceBreakdown={marketplaceChartActive} />
                        </Box>
                    ) : (
                        <Box opacity={isRefetching ? 0.55 : 1} transition="opacity 0.15s">
                            {renderTable(true)}
                        </Box>
                    )}
                </Box>
            ) : (
                cardShell
            )}
        </ReportingFullScreenShell>
    );
};

export default DomainRankingSection;
